'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Play, Pause, Volume2, VolumeX, Volume1, ExternalLink,
  SkipForward, SkipBack, Music2, ChevronDown, ChevronUp,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import Link from 'next/link';
import { songService, Song } from '@/lib/services/radioService';
import { createClient } from '@/lib/supabase/client';

// ── DJ Jorge ElevenLabs config ────────────────────────────────────────────────
const JORGE_VOICE_ID = 'YExhVa4bZONzeingloMX';
const ELEVENLABS_MODEL = 'eleven_multilingual_v2';
const DJ_INTERVAL = 3; // announce every N songs
const JINGLE_URL = 'https://rumbaexplosion.co/wp-content/uploads/2024/10/ElevenLabs_2026-07-28T23_18_24_Charlie-Deep-Confident-Energetic_pre_sp100_s50_sb75_v3-1.mp3';
const WEATHER_INTERVAL = 5; // play weather announcement every N songs
const STATION_JINGLE_URL = 'https://rumbaexplosion.co/wp-content/uploads/2024/10/BestStationOnThePlanet1-1.mp3';
const STATION_JINGLE_INTERVAL = 10; // play station jingle every N songs
const HITS_JINGLE_URL = 'https://rumbaexplosion.co/wp-content/uploads/2024/10/PlayingNuttinButDaHits1-2.mp3';
const HITS_JINGLE_INTERVAL = 10; // play hits jingle every N songs
// ── News bulletin config ──────────────────────────────────────────────────────
const NEWS_BULLETIN_HOUR = 2; // 12:30PM Medellín time (America/Bogota = UTC-5)
const NEWS_BULLETIN_STORAGE_KEY = 'rumba_news_bulletin_last_played_date';

// ── Direct MP3 URL for news bulletin ─────────────────────────────────────────
const NEWS_BULLETIN_MP3_URL = 'https://decisive-canary-357.convex.cloud/api/storage/4cb8891a-e731-44dc-90c7-903c74d1f90d';

// ── Fetch latest news MP3 URL from Supabase ───────────────────────────────────
async function fetchNewsMp3UrlFromSupabase(): Promise<string> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('station_settings')
      .select('news_mp3_url')
      .limit(1)
      .single();
    if (!error && data?.news_mp3_url && data.news_mp3_url.trim() !== '') {
      return data.news_mp3_url.trim();
    }
  } catch {
    // fall through to default
  }
  return NEWS_BULLETIN_MP3_URL;
}

// ── Check if news bulletin should play ───────────────────────────────────────
// Reads scheduled slots from localStorage (set by NewsBulletinContent).
// Falls back to the hardcoded 12:00 PM daily trigger when no slots are stored.
// Uses a 3-minute trigger window to avoid missing slots due to check interval timing.
const NEWS_BULLETIN_PLAYED_KEY = 'rumba_news_bulletin_played_slots';

function shouldPlayNewsBulletin(): boolean {
  if (typeof window === 'undefined') return false;

  // Get current Medellín time (America/Bogota = UTC-5, no DST)
  const now = new Date();
  const medellinOffset = -5 * 60;
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const medellinDate = new Date(utcMs + medellinOffset * 60000);

  const hour = medellinDate.getHours();
  const minute = medellinDate.getMinutes();
  const todayStr = `${medellinDate.getFullYear()}-${String(medellinDate.getMonth() + 1).padStart(2, '0')}-${String(medellinDate.getDate()).padStart(2, '0')}`;
  const nowTotalMin = hour * 60 + minute;

  // ── Check scheduled slots (every-30-min mode) ──────────────────────────
  const slotsRaw = localStorage.getItem('rumba_news_bulletin_schedule_slots');
  if (slotsRaw) {
    try {
      const slots: { date: string; hour: number; minute: number }[] = JSON.parse(slotsRaw);
      if (slots.length > 0) {
        const next = slots[0];
        const slotTotalMin = next.hour * 60 + next.minute;
        const slotKey = `${next.date}_${String(next.hour).padStart(2,'0')}${String(next.minute).padStart(2,'0')}`;

        // Trigger within a 3-minute window AFTER the scheduled time (0 to +3 min)
        const isWithinWindow = next.date === todayStr &&
          nowTotalMin >= slotTotalMin &&
          nowTotalMin <= slotTotalMin + 3;

        if (isWithinWindow) {
          // Check if we already played this slot
          const playedRaw = localStorage.getItem(NEWS_BULLETIN_PLAYED_KEY);
          const played: string[] = playedRaw ? JSON.parse(playedRaw) : [];
          if (played.includes(slotKey)) {
            // Already played — prune if slot is now > 5 min past
            if (nowTotalMin > slotTotalMin + 5) {
              const remaining = slots.slice(1);
              if (remaining.length > 0) {
                localStorage.setItem('rumba_news_bulletin_schedule_slots', JSON.stringify(remaining));
              } else {
                localStorage.removeItem('rumba_news_bulletin_schedule_slots');
              }
            }
            return false;
          }

          // Mark as played immediately to prevent double-firing
          played.push(slotKey);
          // Keep only last 10 played keys to avoid unbounded growth
          const trimmed = played.slice(-10);
          localStorage.setItem(NEWS_BULLETIN_PLAYED_KEY, JSON.stringify(trimmed));

          // Remove this slot from the queue
          const remaining = slots.slice(1);
          if (remaining.length > 0) {
            localStorage.setItem('rumba_news_bulletin_schedule_slots', JSON.stringify(remaining));
          } else {
            localStorage.removeItem('rumba_news_bulletin_schedule_slots');
          }
          console.log(`[News Bulletin] Scheduled slot triggered: ${next.date} ${String(next.hour).padStart(2,'0')}:${String(next.minute).padStart(2,'0')} (now ${String(hour).padStart(2,'0')}:${String(minute).padStart(2,'0')})`);
          return true;
        }

        // Remove stale past slots (more than 5 minutes past)
        const slotIsPast = next.date < todayStr || (next.date === todayStr && slotTotalMin < nowTotalMin - 5);
        if (slotIsPast) {
          const remaining = slots.slice(1);
          if (remaining.length > 0) {
            localStorage.setItem('rumba_news_bulletin_schedule_slots', JSON.stringify(remaining));
          } else {
            localStorage.removeItem('rumba_news_bulletin_schedule_slots');
          }
        }
        return false;
      }
    } catch {
      localStorage.removeItem('rumba_news_bulletin_schedule_slots');
    }
  }

  // ── Fallback: hardcoded 12:00 PM daily trigger ─────────────────────────
  if (hour !== NEWS_BULLETIN_HOUR || minute !== 0) return false;

  const lastPlayed = localStorage.getItem(NEWS_BULLETIN_STORAGE_KEY);
  if (lastPlayed === todayStr) return false;

  localStorage.setItem(NEWS_BULLETIN_STORAGE_KEY, todayStr);
  return true;
}

// ── Medellín weather data fetcher ─────────────────────────────────────────────
const WEATHER_LAT = 6.2442;
const WEATHER_LON = -75.5812;

interface WeatherCondition {
  temperature: number;
  feelsLike: number;
  humidity: number;
  windspeed: number;
  weatherCode: number;
  isDay: number;
}

function getWeatherLabel(code: number, isDay: number): string {
  if (code === 0) return isDay ? 'cielo despejado' : 'noche clara';
  if (code <= 2) return 'parcialmente nublado';
  if (code === 3) return 'nublado';
  if (code <= 49) return 'con niebla';
  if (code <= 59) return 'con llovizna';
  if (code <= 69) return 'con lluvia';
  if (code <= 79) return 'con nieve';
  if (code <= 82) return 'con aguacero';
  if (code <= 84) return 'con granizo';
  if (code <= 99) return 'con tormenta eléctrica';
  return 'variable';
}

async function fetchMedellinWeather(): Promise<WeatherCondition | null> {
  try {
    const weatherController = new AbortController();
    const weatherTimeoutId = setTimeout(() => weatherController.abort(), 6000);
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${WEATHER_LAT}&longitude=${WEATHER_LON}&current=temperature_2m,apparent_temperature,relative_humidity_2m,wind_speed_10m,weather_code,is_day&timezone=America%2FBogota`,
      { signal: weatherController.signal }
    );
    clearTimeout(weatherTimeoutId);
    if (!res.ok) return null;
    const json = await res.json();
    const c = json.current;
    return {
      temperature: Math.round(c.temperature_2m),
      feelsLike: Math.round(c.apparent_temperature),
      humidity: c.relative_humidity_2m,
      windspeed: Math.round(c.wind_speed_10m),
      weatherCode: c.weather_code,
      isDay: c.is_day,
    };
  } catch {
    return null;
  }
}

async function generateWeatherAnnouncement(): Promise<string | null> {
  const apiKey = process.env.NEXT_PUBLIC_ELEVENLABS_API_KEY;
  if (!apiKey) {
    console.warn('[Weather TTS] No ElevenLabs API key found');
    return null;
  }

  const weather = await fetchMedellinWeather();
  if (!weather) {
    console.warn('[Weather TTS] Could not fetch weather data');
    return null;
  }

  const label = getWeatherLabel(weather.weatherCode, weather.isDay);
  const timeOfDay = weather.isDay ? 'día' : 'noche';

  const text = `Y ahora, el reporte del clima para Medellín, la Ciudad de la Eterna Primavera. En este momento tenemos ${label}, con una temperatura de ${weather.temperature} grados centígrados. La sensación térmica es de ${weather.feelsLike} grados. La humedad relativa está al ${weather.humidity} por ciento, y los vientos soplan a ${weather.windspeed} kilómetros por hora. Disfruten esta hermosa ${timeOfDay} en Medellín. Seguimos con más música en Rumbaexplosions.`;

  try {
    const res = await fetch(
      `https://api.elevenlabs.io/v1/text-to-speech/${JORGE_VOICE_ID}`,
      {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          model_id: ELEVENLABS_MODEL,
          voice_settings: {
            stability: 0.4,
            similarity_boost: 1.0,
            style: 0.0,
            use_speaker_boost: true,
          },
          output_format: 'mp3_44100_128',
        }),
      }
    );

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error('[Weather TTS] ElevenLabs API error:', res.status, errText);
      return null;
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    console.log('[Weather TTS] Audio blob URL created:', url);
    return url;
  } catch (err) {
    console.error('[Weather TTS] fetch error:', err);
    return null;
  }
}

async function generateJorgeAnnouncement(nextSong: Song): Promise<string | null> {
  const apiKey = process.env.NEXT_PUBLIC_ELEVENLABS_API_KEY;
  if (!apiKey) {
    console.warn('[DJ Jorge] No ElevenLabs API key found');
    return null;
  }

  const text = `¡Qué tal, amigos! Aquí Jorge, su DJ favorito en Rumbaexplosions, la estación que hace vibrar tu corazón. A continuación, les traigo una joya musical: "${nextSong.title}" de ${nextSong.artist}. ¡Que lo disfruten!`;

  try {
    const res = await fetch(
      `https://api.elevenlabs.io/v1/text-to-speech/${JORGE_VOICE_ID}`,
      {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          model_id: ELEVENLABS_MODEL,
          voice_settings: {
            stability: 0.4,
            similarity_boost: 1.0,
            style: 0.0,
            use_speaker_boost: true,
          },
          output_format: 'mp3_44100_128',
        }),
      }
    );

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error('[DJ Jorge] ElevenLabs API error:', res.status, errText);
      return null;
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    console.log('[DJ Jorge] Audio blob URL created:', url);
    return url;
  } catch (err) {
    console.error('[DJ Jorge] fetch error:', err);
    return null;
  }
}

// ── Generate News Bulletin TTS via ElevenLabs ─────────────────────────────────
async function generateNewsBulletin(): Promise<string | null> {
  // Call the server-side news-tts route which fetches live headlines + generates fresh TTS audio
  try {
    console.log('[News Bulletin] Fetching fresh news TTS from /api/news-tts…');
    const newsController = new AbortController();
    const newsTimeoutId = setTimeout(() => newsController.abort(), 30000);
    const res = await fetch('/api/news-tts', { signal: newsController.signal });
    clearTimeout(newsTimeoutId);
    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      console.error('[News Bulletin] /api/news-tts error:', res.status, errJson);
      return null;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    console.log('[News Bulletin] Fresh TTS blob URL created:', url);
    return url;
  } catch (err) {
    console.error('[News Bulletin] fetch error:', err);
    return null;
  }
}

// ── iTunes artwork fetcher ────────────────────────────────────────────────────
async function fetchItunesArtwork(artist: string, title: string): Promise<string> {
  try {
    const q = encodeURIComponent(`${artist} ${title}`.trim());
    const itunesController = new AbortController();
    const itunesTimeoutId = setTimeout(() => itunesController.abort(), 4000);
    const res = await fetch(
      `https://itunes.apple.com/search?term=${q}&media=music&entity=song&limit=1`,
      { signal: itunesController.signal }
    );
    clearTimeout(itunesTimeoutId);
    if (!res.ok) return '';
    const json = await res.json();
    const result = json?.results?.[0];
    if (!result?.artworkUrl100) return '';
    return result.artworkUrl100.replace('100x100bb', '600x600bb');
  } catch {
    return '';
  }
}

// ── Deterministic gradient fallback ──────────────────────────────────────────
function getArtColor(str: string): [string, string] {
  const colors: [string, string][] = [
    ['#7c3aed', '#a855f7'],
    ['#2563eb', '#7c3aed'],
    ['#db2777', '#7c3aed'],
    ['#059669', '#2563eb'],
    ['#d97706', '#db2777'],
    ['#0891b2', '#059669'],
    ['#7c3aed', '#db2777'],
    ['#1d4ed8', '#0891b2'],
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(artist: string, title: string): string {
  return (artist || title || '?')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PublicPlayerHero() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const nextAudioRef = useRef<HTMLAudioElement | null>(null);
  const djAudioRef = useRef<HTMLAudioElement | null>(null);
  const jingleAudioRef = useRef<HTMLAudioElement | null>(null);
  const stationJingleAudioRef = useRef<HTMLAudioElement | null>(null);
  const hitsJingleAudioRef = useRef<HTMLAudioElement | null>(null);

  const [songs, setSongs] = useState<Song[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(80);
  const [muted, setMuted] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [djSpeaking, setDjSpeaking] = useState(false);
  const [weatherSpeaking, setWeatherSpeaking] = useState(false);
  const [newsSpeaking, setNewsSpeaking] = useState(false);
  // Gate autoplay behind a user gesture to comply with browser autoplay policy
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  // Track whether the initial songs fetch has completed (success or failure)
  const [songsLoaded, setSongsLoaded] = useState(false);

  // artwork: keyed by song id → resolved URL (or '' for fallback)
  const [artworkCache, setArtworkCache] = useState<Record<string, string>>({});
  const fetchingRef = useRef<Set<string>>(new Set());

  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const songPlayCountRef = useRef(0);
  const djBlobUrlRef = useRef<string | null>(null);
  const weatherSongCountRef = useRef(0);
  const stationJingleCountRef = useRef(0);
  const hitsJingleCountRef = useRef(0);
  const earlyPrebufferRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable refs for volume and muted — avoids recreating loadAndPlay on every volume change
  const volumeRef = useRef(80);
  const mutedRef = useRef(false);

  // Pre-fetched TTS blobs — generated during current track to eliminate gap
  const prefetchedDjBlobRef = useRef<string | null>(null);
  const prefetchedWeatherBlobRef = useRef<string | null>(null);
  const prefetchedNewsBlobRef = useRef<string | null>(null);
  const ttsPreFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const weatherAudioRef = useRef<HTMLAudioElement | null>(null);
  const weatherBlobUrlRef = useRef<string | null>(null);

  // ── News bulletin refs ────────────────────────────────────────────────────
  const newsAudioRef = useRef<HTMLAudioElement | null>(null);
  const newsBlobUrlRef = useRef<string | null>(null);
  const newsBulletinPendingRef = useRef(false);
  const newsCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPlayingNewsRef = useRef(false);
  const newsMp3UrlRef = useRef<string>(NEWS_BULLETIN_MP3_URL);
  const newsMp3UrlLastFetchRef = useRef<number>(0);

  // ── Near-end detection guard (prevents double-advance) ───────────────────
  const nearEndFiredRef = useRef(false);
  // ── Crossfade state ───────────────────────────────────────────────────────
  const crossfadeAnimRef = useRef<number | null>(null);
  // ── Watchdog: detects silent stalls and auto-recovers ─────────────────────
  const watchdogRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastKnownTimeRef = useRef<number>(0);
  const lastKnownTimeStampRef = useRef<number>(0);

  // ── Fetch songs ───────────────────────────────────────────────────────────
  useEffect(() => {
    songService.getAll().then((all) => {
      const playable = all.filter((s) => s.fileUrl && s.fileUrl.trim() !== '');
      for (let i = playable.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [playable[i], playable[j]] = [playable[j], playable[i]];
      }
      setSongs(playable);
      setSongsLoaded(true);
    }).catch(() => {
      setSongs([]);
      setSongsLoaded(true);
    });

    // Safety timeout: dismiss loading overlay after 10s regardless of fetch result
    const safetyTimer = setTimeout(() => setSongsLoaded(true), 10000);
    return () => clearTimeout(safetyTimer);
  }, []);

  // ── Fetch and refresh news MP3 URL from Supabase every 30 minutes ─────────
  useEffect(() => {
    const refreshNewsUrl = async () => {
      const url = await fetchNewsMp3UrlFromSupabase();
      newsMp3UrlRef.current = url;
      newsMp3UrlLastFetchRef.current = Date.now();
      console.log('[News Bulletin] Refreshed MP3 URL from Supabase:', url);
    };

    // Fetch immediately on mount
    refreshNewsUrl();

    // Refresh every 30 minutes
    const intervalId = setInterval(refreshNewsUrl, 30 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  // ── Elapsed ticker ────────────────────────────────────────────────────────
  const startTicker = useCallback(() => {
    if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    elapsedIntervalRef.current = setInterval(() => {
      if (audioRef.current && !audioRef.current.paused) {
        setElapsed(audioRef.current.currentTime);
        setDuration(audioRef.current.duration || 0);
      }
    }, 500);
  }, []);

  const stopTicker = useCallback(() => {
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
  }, []);

  useEffect(() => () => stopTicker(), [stopTicker]);

  // Cleanup crossfade animation on unmount
  useEffect(() => () => {
    if (crossfadeAnimRef.current !== null) {
      clearInterval(crossfadeAnimRef.current);
      crossfadeAnimRef.current = null;
    }
  }, []);

  // ── Pre-buffer next track ─────────────────────────────────────────────────
  const prebufferNext = useCallback((nextIndex: number, songList: Song[]) => {
    const nextSong = songList[nextIndex];
    if (!nextSong?.fileUrl) return;
    const nextAudio = nextAudioRef.current;
    if (!nextAudio) return;
    if (nextAudio.src !== nextSong.fileUrl) {
      nextAudio.src = nextSong.fileUrl;
      nextAudio.preload = 'auto';
      nextAudio.load();
    }
  }, []);

  // ── Play Jingle ───────────────────────────────────────────────────────────
  const playJingle = useCallback((volumeLevel: number, isMuted: boolean): Promise<void> => {
    return new Promise((resolve) => {
      const jingleAudio = jingleAudioRef.current;
      if (!jingleAudio) { resolve(); return; }

      jingleAudio.volume = isMuted ? 0 : Math.max(0, Math.min(1, volumeLevel / 100));
      jingleAudio.currentTime = 0;

      const cleanup = () => {
        jingleAudio.onended = null;
        jingleAudio.onerror = null;
      };

      jingleAudio.onended = () => { cleanup(); resolve(); };
      jingleAudio.onerror = () => { cleanup(); resolve(); };

      // Only set src + load if not already loaded
      if (!jingleAudio.src || !jingleAudio.src.includes('ElevenLabs_2026')) {
        jingleAudio.src = JINGLE_URL;
        jingleAudio.load();
      }
      jingleAudio.play().catch(() => { cleanup(); resolve(); });
    });
  }, []);

  // ── Play Station Jingle ───────────────────────────────────────────────────
  const playStationJingle = useCallback((volumeLevel: number, isMuted: boolean): Promise<void> => {
    return new Promise((resolve) => {
      const stationJingle = stationJingleAudioRef.current;
      if (!stationJingle) { resolve(); return; }

      stationJingle.volume = isMuted ? 0 : Math.max(0, Math.min(1, volumeLevel / 100));
      stationJingle.currentTime = 0;

      const cleanup = () => {
        stationJingle.onended = null;
        stationJingle.onerror = null;
      };

      stationJingle.onended = () => { cleanup(); resolve(); };
      stationJingle.onerror = () => { cleanup(); resolve(); };

      if (!stationJingle.src || !stationJingle.src.includes('BestStation')) {
        stationJingle.src = STATION_JINGLE_URL;
        stationJingle.load();
      }
      stationJingle.play().catch(() => { cleanup(); resolve(); });
    });
  }, []);

  // ── Play Hits Jingle (every 15 songs) ────────────────────────────────────
  const playHitsJingle = useCallback((volumeLevel: number, isMuted: boolean): Promise<void> => {
    return new Promise((resolve) => {
      const hitsJingle = hitsJingleAudioRef.current;
      if (!hitsJingle) { resolve(); return; }

      hitsJingle.volume = isMuted ? 0 : Math.max(0, Math.min(1, volumeLevel / 100));
      hitsJingle.currentTime = 0;

      const cleanup = () => {
        hitsJingle.onended = null;
        hitsJingle.onerror = null;
      };

      hitsJingle.onended = () => { cleanup(); resolve(); };
      hitsJingle.onerror = () => { cleanup(); resolve(); };

      if (!hitsJingle.src || !hitsJingle.src.includes('PlayingNuttin')) {
        hitsJingle.src = HITS_JINGLE_URL;
        hitsJingle.load();
      }
      hitsJingle.play().catch(() => { cleanup(); resolve(); });
    });
  }, []);

  // ── Play Weather Announcement via ElevenLabs TTS ─────────────────────────
  const playWeatherAnnouncement = useCallback(async (volumeLevel: number, isMuted: boolean): Promise<void> => {
    // Use pre-fetched blob if available (generated during previous track)
    let blobUrl = prefetchedWeatherBlobRef.current;
    prefetchedWeatherBlobRef.current = null;

    if (!blobUrl) {
      console.log('[Weather TTS] No pre-fetched blob, generating now...');
      blobUrl = await generateWeatherAnnouncement();
    } else {
      console.log('[Weather TTS] Using pre-fetched weather blob — zero gap');
    }

    if (!blobUrl) {
      console.warn('[Weather TTS] No blob URL returned, skipping weather announcement');
      return;
    }

    if (weatherBlobUrlRef.current) {
      URL.revokeObjectURL(weatherBlobUrlRef.current);
    }
    weatherBlobUrlRef.current = blobUrl;

    return new Promise((resolve) => {
      const weatherAudio = weatherAudioRef.current;
      if (!weatherAudio) { resolve(); return; }

      weatherAudio.src = blobUrl!;
      weatherAudio.volume = isMuted ? 0 : 1.0;
      weatherAudio.currentTime = 0;

      const cleanup = () => {
        weatherAudio.onended = null;
        weatherAudio.onerror = null;
      };

      weatherAudio.onended = () => {
        console.log('[Weather TTS] Weather announcement finished');
        cleanup();
        setWeatherSpeaking(false);
        resolve();
      };

      weatherAudio.onerror = (e) => {
        console.error('[Weather TTS] Audio playback error:', e);
        cleanup();
        setWeatherSpeaking(false);
        resolve();
      };

      weatherAudio.play().then(() => {
        setWeatherSpeaking(true);
      }).catch((err) => {
        console.error('[Weather TTS] play() failed:', err);
        setWeatherSpeaking(false);
        resolve();
      });
    });
  }, []);

  // ── Play News Bulletin from direct MP3 URL ────────────────────────────────
  const playNewsBulletin = useCallback(async (volumeLevel: number, isMuted: boolean): Promise<void> => {
    console.log('[News Bulletin] Generating fresh Spanish TTS via /api/news-tts…');

    // Generate fresh Spanish TTS audio (same as the scheduler path)
    let blobUrl = await generateNewsBulletin();
    if (!blobUrl) {
      console.warn('[News Bulletin] TTS generation failed, skipping bulletin');
      return;
    }

    if (newsBlobUrlRef.current) {
      URL.revokeObjectURL(newsBlobUrlRef.current);
    }
    newsBlobUrlRef.current = blobUrl;

    return new Promise((resolve) => {
      const newsAudio = newsAudioRef.current;
      if (!newsAudio) { resolve(); return; }

      newsAudio.src = blobUrl;
      newsAudio.volume = isMuted ? 0 : 1.0;
      newsAudio.currentTime = 0;

      const cleanup = () => {
        newsAudio.onended = null;
        newsAudio.onerror = null;
      };

      newsAudio.onended = () => {
        console.log('[News Bulletin] Bulletin finished');
        cleanup();
        setNewsSpeaking(false);
        resolve();
      };

      newsAudio.onerror = (e) => {
        console.error('[News Bulletin] Audio playback error:', e);
        cleanup();
        setNewsSpeaking(false);
        resolve();
      };

      newsAudio.load();
      const onCanPlay = () => {
        newsAudio.removeEventListener('canplay', onCanPlay);
        newsAudio.play().then(() => {
          setNewsSpeaking(true);
          console.log('[News Bulletin] Playing Spanish bulletin now');
        }).catch((err) => {
          console.error('[News Bulletin] play() failed:', err);
          setNewsSpeaking(false);
          resolve();
        });
      };
      newsAudio.addEventListener('canplay', onCanPlay, { once: true });
    });
  }, []);

  // ── Play DJ Jorge announcement ────────────────────────────────────────────
  const playDjAnnouncement = useCallback(async (nextSong: Song, volumeLevel: number, isMuted: boolean): Promise<void> => {
    // Use pre-fetched blob if available (generated during previous track)
    let blobUrl = prefetchedDjBlobRef.current;
    prefetchedDjBlobRef.current = null;

    if (!blobUrl) {
      console.log('[DJ Jorge] No pre-fetched blob, generating now for:', nextSong.title);
      blobUrl = await generateJorgeAnnouncement(nextSong);
    } else {
      console.log('[DJ Jorge] Using pre-fetched DJ blob — zero gap');
    }

    if (!blobUrl) {
      console.warn('[DJ Jorge] No blob URL returned, skipping announcement');
      return;
    }

    if (djBlobUrlRef.current) {
      URL.revokeObjectURL(djBlobUrlRef.current);
    }
    djBlobUrlRef.current = blobUrl;

    return new Promise((resolve) => {
      const djAudio = djAudioRef.current;
      if (!djAudio) {
        console.warn('[DJ Jorge] djAudioRef not available');
        resolve();
        return;
      }

      djAudio.src = blobUrl!;
      djAudio.volume = isMuted ? 0 : Math.max(0, Math.min(1, volumeLevel / 100));
      djAudio.currentTime = 0;
      setDjSpeaking(true);

      const cleanup = () => {
        djAudio.onended = null;
        djAudio.onerror = null;
      };

      djAudio.onended = () => {
        console.log('[DJ Jorge] Announcement finished');
        cleanup();
        setDjSpeaking(false);
        resolve();
      };

      djAudio.onerror = (e) => {
        console.error('[DJ Jorge] Audio playback error:', e);
        cleanup();
        setDjSpeaking(false);
        resolve();
      };

      djAudio.play().then(() => {
        console.log('[DJ Jorge] Playing announcement');
      }).catch((err) => {
        console.error('[DJ Jorge] play() failed:', err);
        setDjSpeaking(false);
        resolve();
      });
    });
  }, []);

  // ── Load and play ─────────────────────────────────────────────────────────
  const loadAndPlay = useCallback(async (index: number, songList?: Song[], useBuffered?: boolean) => {
    const list = songList ?? songs;
    const song = list[index];
    if (!song?.fileUrl) { setLoadError('No audio file available.'); return; }

    const audio = audioRef.current;
    if (!audio) return;

    // Read volume/muted from stable refs to avoid stale closure issues
    const vol = volumeRef.current;
    const isMuted = mutedRef.current;

    // Clear any pending early-prebuffer timer
    if (earlyPrebufferRef.current) {
      clearTimeout(earlyPrebufferRef.current);
      earlyPrebufferRef.current = null;
    }
    // Clear any pending TTS pre-fetch timer
    if (ttsPreFetchTimerRef.current) {
      clearTimeout(ttsPreFetchTimerRef.current);
      ttsPreFetchTimerRef.current = null;
    }

    setLoadError(null);
    setIsLoading(true);
    setElapsed(0);
    setDuration(0);
    stopTicker();
    // Reset near-end guard for the new track
    nearEndFiredRef.current = false;

    // Check if DJ Jorge should announce (every DJ_INTERVAL songs)
    songPlayCountRef.current += 1;
    const shouldAnnounce = songPlayCountRef.current % DJ_INTERVAL === 0;

    // Play weather MP3 every WEATHER_INTERVAL songs
    weatherSongCountRef.current += 1;
    const shouldPlayWeather = weatherSongCountRef.current % WEATHER_INTERVAL === 0;

    // Play station jingle every STATION_JINGLE_INTERVAL songs
    stationJingleCountRef.current += 1;
    const shouldPlayStationJingle = stationJingleCountRef.current % STATION_JINGLE_INTERVAL === 0;

    // Play hits jingle every HITS_JINGLE_INTERVAL songs
    hitsJingleCountRef.current += 1;
    const shouldPlayHitsJingle = hitsJingleCountRef.current % HITS_JINGLE_INTERVAL === 0;

    // Check if daily news bulletin is pending
    const shouldPlayNewsBulletinNow = newsBulletinPendingRef.current;
    if (shouldPlayNewsBulletinNow) {
      newsBulletinPendingRef.current = false;
    }

    const hasInterstitial = shouldAnnounce || shouldPlayWeather || shouldPlayStationJingle || shouldPlayHitsJingle || shouldPlayNewsBulletinNow;

    if (shouldAnnounce) {
      audio.pause();
      // Play jingle first, then DJ announcement
      await playJingle(vol, isMuted);
      await playDjAnnouncement(song, vol, isMuted);
    }

    if (shouldPlayWeather) {
      if (!shouldAnnounce) audio.pause();
      // Play weather MP3 (someone reads the weather out loud)
      await playWeatherAnnouncement(vol, isMuted);
    }

    if (shouldPlayStationJingle) {
      if (!shouldAnnounce && !shouldPlayWeather) audio.pause();
      await playStationJingle(vol, isMuted);
    }

    if (shouldPlayHitsJingle) {
      if (!shouldAnnounce && !shouldPlayWeather && !shouldPlayStationJingle) audio.pause();
      await playHitsJingle(vol, isMuted);
    }

    if (shouldPlayNewsBulletinNow) {
      if (!shouldAnnounce && !shouldPlayWeather && !shouldPlayStationJingle && !shouldPlayHitsJingle) audio.pause();
      await playNewsBulletin(vol, isMuted);
    }

    // ── Gapless playback: use pre-buffered audio when no interstitial ────────
    const nextAudio = nextAudioRef.current;
    const canUseBuffered = useBuffered &&
      !hasInterstitial &&
      nextAudio &&
      nextAudio.src === song.fileUrl &&
      nextAudio.readyState >= 3;

    if (canUseBuffered && nextAudio) {
      // Gapless swap: transfer the pre-buffered src directly to main audio
      // without calling load() — browser keeps the decoded buffer in memory
      audio.src = song.fileUrl;
      audio.currentTime = 0;
      audio.volume = isMuted ? 0 : vol / 100;
      // Clear the pre-buffer src so it can be reused for the next track
      nextAudio.src = '';
    } else {
      if (hasInterstitial) {
        // Already paused above during interstitial
      } else {
        audio.pause();
      }
      audio.src = song.fileUrl;
      audio.currentTime = 0;
      audio.volume = isMuted ? 0 : vol / 100;
      // Always call load() and wait for canplay before play() to ensure
      // reliable startup on cold load / published domain (fixes first-track issue)
      audio.load();
      const canPlayResult = await new Promise<'ready' | 'error'>((res) => {
        let settled = false;
        let timeoutId: ReturnType<typeof setTimeout>;
        const onError = () => {
          if (settled) return;
          settled = true;
          audio.removeEventListener('canplay', onReady);
          audio.removeEventListener('error', onError);
          clearTimeout(timeoutId);
          res('error');
        };
        const onReady = () => {
          if (settled) return;
          settled = true;
          audio.removeEventListener('canplay', onReady);
          audio.removeEventListener('error', onError);
          clearTimeout(timeoutId);
          res('ready');
        };
        // Timeout after 12s — treat as error so we can skip to next track
        timeoutId = setTimeout(() => {
          if (settled) return;
          settled = true;
          audio.removeEventListener('canplay', onReady);
          audio.removeEventListener('error', onError);
          console.warn('[Player] canplay timeout for:', song.fileUrl);
          res('error');
        }, 12000);
        audio.addEventListener('canplay', onReady, { once: true });
        audio.addEventListener('error', onError, { once: true });
      });

      // If audio failed to load, skip to next track automatically
      if (canPlayResult === 'error') {
        console.warn('[Player] Audio load failed, auto-advancing to next track:', song.fileUrl);
        setIsLoading(false);
        setIsPlaying(false);
        if (list.length > 1) {
          const nextIdx = (index + 1) % list.length;
          setCurrentIndex(nextIdx);
          // Small delay to avoid rapid-fire skipping
          setTimeout(() => loadAndPlay(nextIdx, list, false), 500);
        }
        return;
      }
    }

    try {
      await audio.play();
      setIsPlaying(true);
      setIsLoading(false);
      startTicker();

      const nextIdx = (index + 1) % list.length;
      prebufferNext(nextIdx, list);

      // ── Schedule TTS pre-fetch + early audio prebuffer ────────────────────
      // Pre-fetch TTS 30s before end so it's ready with zero gap
      const scheduleTtsPrefetch = (dur: number) => {
        if (ttsPreFetchTimerRef.current) clearTimeout(ttsPreFetchTimerRef.current);
        const nextSongForAnnounce = list[nextIdx];
        const nextCount = songPlayCountRef.current + 1;
        const willAnnounce = nextCount % DJ_INTERVAL === 0;
        const willWeather = (weatherSongCountRef.current + 1) % WEATHER_INTERVAL === 0;

        const msToPrefetch = Math.max(0, (dur - 30) * 1000 - audio.currentTime * 1000);
        ttsPreFetchTimerRef.current = setTimeout(() => {
          if (willAnnounce && nextSongForAnnounce && !prefetchedDjBlobRef.current) {
            console.log('[DJ Jorge] Pre-fetching announcement during track...');
            generateJorgeAnnouncement(nextSongForAnnounce).then((url) => {
              if (url) prefetchedDjBlobRef.current = url;
            });
          }
          if (willWeather && !prefetchedWeatherBlobRef.current) {
            console.log('[Weather TTS] Pre-fetching weather during track...');
            generateWeatherAnnouncement().then((url) => {
              if (url) prefetchedWeatherBlobRef.current = url;
            });
          }
          // News bulletin uses a direct MP3 URL — no pre-fetch needed
        }, msToPrefetch);
      };

      // Schedule early prebuffer: start buffering 15s before end
      const scheduledDuration = audio.duration;
      if (isFinite(scheduledDuration) && scheduledDuration > 20) {
        const msUntilEarlyBuffer = (scheduledDuration - 15) * 1000;
        earlyPrebufferRef.current = setTimeout(() => {
          prebufferNext(nextIdx, list);
        }, msUntilEarlyBuffer);
        scheduleTtsPrefetch(scheduledDuration);
      } else {
        // Schedule after metadata loads
        const onMeta = () => {
          audio.removeEventListener('loadedmetadata', onMeta);
          const dur = audio.duration;
          if (isFinite(dur) && dur > 20) {
            const ms = (dur - 15) * 1000 - audio.currentTime * 1000;
            if (ms > 0) {
              earlyPrebufferRef.current = setTimeout(() => {
                prebufferNext(nextIdx, list);
              }, ms);
            }
            scheduleTtsPrefetch(dur);
          }
        };
        audio.addEventListener('loadedmetadata', onMeta);
      }
    } catch (err: unknown) {
      // If the browser blocked autoplay (NotAllowedError), do NOT skip tracks.
      // Instead, reset loading state and wait for the user to press Play.
      const isAutoplayBlocked =
        err instanceof DOMException &&
        (err.name === 'NotAllowedError' || err.name === 'NotSupportedError');

      if (isAutoplayBlocked) {
        console.warn('[Player] Autoplay blocked by browser — waiting for user gesture');
        setIsPlaying(false);
        setIsLoading(false);
        // Reset so the user can press Play and it will work
        return;
      }

      console.warn('[Player] audio.play() failed, auto-advancing to next track:', song.fileUrl);
      setIsPlaying(false);
      setIsLoading(false);
      // Auto-advance to next track instead of showing error
      if (list.length > 1) {
        const nextIdx = (index + 1) % list.length;
        setCurrentIndex(nextIdx);
        setTimeout(() => loadAndPlay(nextIdx, list, false), 800);
      } else {
        setLoadError('Could not play this track. Try another.');
      }
    }
  }, [songs, startTicker, stopTicker, prebufferNext, playDjAnnouncement, playJingle, playWeatherAnnouncement, playStationJingle, playHitsJingle, playNewsBulletin]);

  // ── Autoplay on load ──────────────────────────────────────────────────────
  const autoplayedRef = useRef(false);
  useEffect(() => {
    // Only autoplay once songs are loaded AND the user has tapped/clicked to start
    if (songs.length > 0 && userHasInteracted && !autoplayedRef.current) {
      autoplayedRef.current = true;
      loadAndPlay(0, songs);
    }
  }, [songs, userHasInteracted, loadAndPlay]);

  // ── News bulletin scheduler ───────────────────────────────────────────────
  useEffect(() => {
    // Check every 15 seconds so we don't miss a scheduled slot within its minute window
    newsCheckIntervalRef.current = setInterval(async () => {
      // Don't fire if news is already playing
      if (isPlayingNewsRef.current) return;

      // Refresh news URL from Supabase if it's been more than 30 minutes since last fetch
      const now = Date.now();
      if (now - newsMp3UrlLastFetchRef.current > 30 * 60 * 1000) {
        fetchNewsMp3UrlFromSupabase().then((url) => {
          newsMp3UrlRef.current = url;
          newsMp3UrlLastFetchRef.current = now;
          console.log('[News Bulletin] Refreshed MP3 URL (interval check):', url);
        });
      }

      if (shouldPlayNewsBulletin()) {
        console.log('[News Bulletin] Scheduled slot matched — generating bulletin while music plays');

        isPlayingNewsRef.current = true;

        // ── Cancel any pending timers that could restart audio ──────────────
        if (earlyPrebufferRef.current) {
          clearTimeout(earlyPrebufferRef.current);
          earlyPrebufferRef.current = null;
        }
        if (ttsPreFetchTimerRef.current) {
          clearTimeout(ttsPreFetchTimerRef.current);
          ttsPreFetchTimerRef.current = null;
        }

        // ── Cancel any in-progress crossfade animation ───────────────────────
        if (crossfadeAnimRef.current !== null) {
          clearInterval(crossfadeAnimRef.current);
          crossfadeAnimRef.current = null;
        }

        const audio = audioRef.current;
        const wasPlaying = audio ? !audio.paused : false;
        const resumeTime = audio ? audio.currentTime : 0;

        // ── Pause music IMMEDIATELY before async TTS generation ──────────────
        if (audio) {
          audio.pause();
        }
        if (nextAudioRef.current) {
          nextAudioRef.current.pause();
          nextAudioRef.current.src = '';
        }
        setIsPlaying(false);
        stopTicker();

        // ── Generate bulletin (music is already paused) ──────────────────────
        let blobUrl = prefetchedNewsBlobRef.current;
        prefetchedNewsBlobRef.current = null;
        if (!blobUrl) {
          console.log('[News Bulletin] No pre-fetched blob, generating now (music paused)...');
          blobUrl = await generateNewsBulletin();
        } else {
          console.log('[News Bulletin] Using pre-fetched news blob — zero gap');
        }

        if (blobUrl) {
          if (newsBlobUrlRef.current) {
            URL.revokeObjectURL(newsBlobUrlRef.current);
          }
          newsBlobUrlRef.current = blobUrl;

          setNewsSpeaking(true);

          await new Promise<void>((resolve) => {
            const newsAudio = newsAudioRef.current;
            if (!newsAudio) { resolve(); return; }

            // Ensure music is still paused right before news plays
            if (audioRef.current) audioRef.current.pause();

            newsAudio.src = blobUrl!;
            newsAudio.volume = mutedRef.current ? 0 : 1.0;
            newsAudio.currentTime = 0;

            const cleanup = () => {
              newsAudio.onended = null;
              newsAudio.onerror = null;
            };

            newsAudio.onended = () => {
              console.log('[News Bulletin] Bulletin finished — resuming music');
              cleanup();
              setNewsSpeaking(false);
              resolve();
            };

            newsAudio.onerror = (e) => {
              console.error('[News Bulletin] Audio playback error:', e);
              cleanup();
              setNewsSpeaking(false);
              resolve();
            };

            // Load then play to avoid gap from src assignment
            newsAudio.load();
            const onCanPlay = () => {
              newsAudio.removeEventListener('canplay', onCanPlay);
              newsAudio.play().then(() => {
                console.log('[News Bulletin] Playing bulletin now');
              }).catch((err) => {
                console.error('[News Bulletin] play() failed:', err);
                setNewsSpeaking(false);
                resolve();
              });
            };
            newsAudio.addEventListener('canplay', onCanPlay, { once: true });
          });
        } else {
          setNewsSpeaking(false);
        }

        // Resume music from where it was paused
        if (audio && wasPlaying) {
          try {
            audio.currentTime = resumeTime;
            await audio.play();
            setIsPlaying(true);
            startTicker();
            console.log('[News Bulletin] Music resumed');
          } catch (err) {
            console.error('[News Bulletin] Failed to resume music:', err);
          }
        }

        isPlayingNewsRef.current = false;
      }
    }, 15000);

    return () => {
      if (newsCheckIntervalRef.current) {
        clearInterval(newsCheckIntervalRef.current);
        newsCheckIntervalRef.current = null;
      }
      if (newsBlobUrlRef.current) {
        URL.revokeObjectURL(newsBlobUrlRef.current);
        newsBlobUrlRef.current = null;
      }
    };
  }, [stopTicker, startTicker]);

  // ── Audio watchdog: detects silent stalls and auto-recovers ───────────────
  // Runs every 10 seconds. If isPlaying is true but the audio element is paused
  // OR currentTime hasn't advanced in 15+ seconds, we restart the current track.
  useEffect(() => {
    if (watchdogRef.current) clearInterval(watchdogRef.current);

    watchdogRef.current = setInterval(() => {
      // Don't interfere while news bulletin is playing
      if (isPlayingNewsRef.current) return;

      const audio = audioRef.current;
      if (!audio) return;

      // Only check when we believe we're playing
      if (!isPlaying) return;

      const now = Date.now();
      const currentTime = audio.currentTime;

      // Case 1: Audio element is paused but state says playing
      if (audio.paused && !audio.ended) {
        console.warn('[Watchdog] Audio stalled (paused while isPlaying=true) — recovering...');
        // Try to resume first
        audio.play().catch(() => {
          // Resume failed — reload the current track
          console.warn('[Watchdog] Resume failed — reloading current track');
          loadAndPlay(currentIndex, undefined, false);
        });
        return;
      }

      // Case 2: currentTime hasn't advanced in 15 seconds (network stall / buffer underrun)
      const timeSinceLastAdvance = now - lastKnownTimeStampRef.current;
      const timeAdvanced = Math.abs(currentTime - lastKnownTimeRef.current);

      if (timeSinceLastAdvance > 15000 && timeAdvanced < 0.5 && !audio.paused) {
        console.warn('[Watchdog] Audio currentTime frozen — possible network stall, recovering...');
        loadAndPlay(currentIndex, undefined, false);
        return;
      }

      // Update last known position
      lastKnownTimeRef.current = currentTime;
      lastKnownTimeStampRef.current = now;
    }, 10000);

    return () => {
      if (watchdogRef.current) {
        clearInterval(watchdogRef.current);
        watchdogRef.current = null;
      }
    };
  }, [isPlaying, currentIndex, loadAndPlay]);

  const currentSong = songs[currentIndex] ?? null;

  // ── Resolve artwork for a song ────────────────────────────────────────────
  const resolveArtwork = useCallback(async (song: Song) => {
    if (!song) return;
    if (artworkCache[song.id] !== undefined) return;
    if (fetchingRef.current.has(song.id)) return;

    fetchingRef.current.add(song.id);

    if (song.albumArtUrl && song.albumArtUrl.trim() !== '') {
      setArtworkCache((prev) => ({ ...prev, [song.id]: song.albumArtUrl }));
      fetchingRef.current.delete(song.id);
      return;
    }

    const url = await fetchItunesArtwork(song.artist, song.title);
    setArtworkCache((prev) => ({ ...prev, [song.id]: url }));
    fetchingRef.current.delete(song.id);
  }, [artworkCache]);

  useEffect(() => {
    if (songs.length === 0) return;
    const toResolve = [
      songs[currentIndex],
      songs[(currentIndex + 1) % songs.length],
      songs[(currentIndex + 2) % songs.length],
    ].filter(Boolean);
    toResolve.forEach((s) => resolveArtwork(s));
  }, [songs, currentIndex, resolveArtwork]);

  // ── Sync volume/mute ──────────────────────────────────────────────────────
  useEffect(() => {
    volumeRef.current = volume;
    mutedRef.current = muted;
    if (!audioRef.current) return;
    audioRef.current.volume = muted ? 0 : volume / 100;
  }, [volume, muted]);

  // ── Sync DJ audio volume/mute ─────────────────────────────────────────────
  useEffect(() => {
    if (!djAudioRef.current) return;
    djAudioRef.current.volume = muted ? 0 : Math.min(1, (volume / 100) * 5.0);
  }, [volume, muted]);

  // ── Sync jingle audio volume/mute ────────────────────────────────────────
  useEffect(() => {
    if (!jingleAudioRef.current) return;
    jingleAudioRef.current.volume = muted ? 0 : volume / 100;
  }, [volume, muted]);

  // ── Sync station jingle audio volume/mute ────────────────────────────────
  useEffect(() => {
    if (!stationJingleAudioRef.current) return;
    stationJingleAudioRef.current.volume = muted ? 0 : volume / 100;
  }, [volume, muted]);

  // ── Sync hits jingle audio volume/mute ───────────────────────────────────
  useEffect(() => {
    if (!hitsJingleAudioRef.current) return;
    hitsJingleAudioRef.current.volume = muted ? 0 : volume / 100;
  }, [volume, muted]);

  // ── Sync weather audio volume/mute ───────────────────────────────────────
  useEffect(() => {
    if (!weatherAudioRef.current) return;
    weatherAudioRef.current.volume = muted ? 0 : volume / 100;
  }, [volume, muted]);

  // ── Pre-load jingles on mount for zero-gap playback ───────────────────────
  useEffect(() => {
    const preloadAudio = (ref: React.MutableRefObject<HTMLAudioElement | null>, url: string) => {
      const el = ref.current;
      if (!el) return;
      el.src = url;
      el.preload = 'auto';
      el.load();
    };
    preloadAudio(jingleAudioRef, JINGLE_URL);
    preloadAudio(stationJingleAudioRef, STATION_JINGLE_URL);
    preloadAudio(hitsJingleAudioRef, HITS_JINGLE_URL);
  }, []);

  // ── Page Visibility recovery: resume audio when tab becomes visible again ──
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (isPlayingNewsRef.current) return;

      const audio = audioRef.current;
      if (!audio) return;

      // If we think we're playing but audio is paused, try to resume
      if (isPlaying && audio.paused && !audio.ended) {
        console.log('[Visibility] Tab became visible — resuming stalled audio');
        audio.play().catch(() => {
          console.warn('[Visibility] Resume failed — reloading current track');
          loadAndPlay(currentIndex, undefined, false);
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPlaying, currentIndex, loadAndPlay]);

  // ── Play/Pause ────────────────────────────────────────────────────────────
  const handlePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) { audio.pause(); setIsPlaying(false); stopTicker(); return; }
    if (audio.src && audio.src !== window.location.href && audio.readyState >= 2) {
      try { await audio.play(); setIsPlaying(true); startTicker(); return; } catch {}
    }
    if (songs.length > 0) await loadAndPlay(currentIndex);
  };

  // ── Skip ──────────────────────────────────────────────────────────────────
  const handleSkipNext = useCallback(async () => {
    if (!songs.length) return;
    const next = (currentIndex + 1) % songs.length;
    setCurrentIndex(next);
    if (isPlaying) await loadAndPlay(next);
  }, [songs, currentIndex, isPlaying, loadAndPlay]);

  const handleSkipPrev = useCallback(async () => {
    if (!songs.length) return;
    const prev = (currentIndex - 1 + songs.length) % songs.length;
    setCurrentIndex(prev);
    if (isPlaying) await loadAndPlay(prev);
  }, [songs, currentIndex, isPlaying, loadAndPlay]);

  // ── Shuffle Queue ─────────────────────────────────────────────────────────
  const handleShuffle = useCallback(() => {
    if (songs.length < 2) return;
    const current = songs[currentIndex];
    const rest = songs.filter((_, i) => i !== currentIndex);
    for (let i = rest.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [rest[i], rest[j]] = [rest[j], rest[i]];
    }
    setSongs([current, ...rest]);
    setCurrentIndex(0);
  }, [songs, currentIndex]);

  // ── Auto-advance ──────────────────────────────────────────────────────────
  const handleEnded = useCallback(async () => {
    // Don't advance to next song if news bulletin is currently playing
    if (isPlayingNewsRef.current) {
      console.log('[News Bulletin] handleEnded suppressed — news is playing');
      return;
    }
    // If near-end detection already fired, skip to avoid double-advance
    if (nearEndFiredRef.current) {
      nearEndFiredRef.current = false;
      return;
    }
    stopTicker();
    setElapsed(0);
    if (songs.length > 1) {
      const next = (currentIndex + 1) % songs.length;
      setCurrentIndex(next);
      // Pass useBuffered=true so loadAndPlay uses pre-buffered audio if available
      await loadAndPlay(next, undefined, true);
    } else {
      setIsPlaying(false);
    }
  }, [songs, currentIndex, loadAndPlay, stopTicker]);

  // ── Near-end timeupdate handler — crossfade 0.3s before track ends ────────
  const handleTimeUpdate = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !isPlaying) return;
    if (isPlayingNewsRef.current) return;
    if (nearEndFiredRef.current) return;

    const { currentTime, duration: dur } = audio;
    if (!isFinite(dur) || dur <= 0) return;

    // Trigger crossfade 0.3s before the end
    const remaining = dur - currentTime;
    if (remaining > 0.3) return;

    // Guard: only fire once per track
    nearEndFiredRef.current = true;

    if (songs.length <= 1) return;

    const next = (currentIndex + 1) % songs.length;
    const nextSong = songs[next];
    if (!nextSong?.fileUrl) return;

    // ── Crossfade: fade-out current + fade-in next simultaneously ────────────
    const FADE_DURATION = 0.3; // seconds
    const FADE_STEPS = 18; // ~60fps over 0.3s
    const STEP_INTERVAL = (FADE_DURATION * 1000) / FADE_STEPS;
    // Read from refs to avoid stale closure
    const vol = volumeRef.current;
    const isMuted = mutedRef.current;
    const startVolume = isMuted ? 0 : vol / 100;

    // Prepare next audio element with the next track
    const nextAudio = nextAudioRef.current;
    if (nextAudio) {
      // If not already pre-buffered, set src now
      if (nextAudio.src !== nextSong.fileUrl) {
        nextAudio.src = nextSong.fileUrl;
        nextAudio.preload = 'auto';
        nextAudio.load();
      }
      nextAudio.volume = 0;
      nextAudio.currentTime = 0;
    }

    // Cancel any existing crossfade animation
    if (crossfadeAnimRef.current !== null) {
      clearInterval(crossfadeAnimRef.current);
      crossfadeAnimRef.current = null;
    }

    // Abort crossfade if news bulletin started during the async TTS generation
    if (isPlayingNewsRef.current) return;

    // Start next track playing immediately (at volume 0)
    if (nextAudio) {
      try {
        await nextAudio.play();
      } catch {
        // If play fails, fall back to standard advance via loadAndPlay
        setCurrentIndex(next);
        stopTicker();
        setElapsed(0);
        await loadAndPlay(next, undefined, false);
        return;
      }
    }

    let step = 0;
    crossfadeAnimRef.current = setInterval(() => {
      // Stop crossfade mid-animation if news bulletin takes over
      if (isPlayingNewsRef.current) {
        if (crossfadeAnimRef.current !== null) {
          clearInterval(crossfadeAnimRef.current);
          crossfadeAnimRef.current = null;
        }
        if (nextAudio) {
          nextAudio.pause();
          nextAudio.src = '';
        }
        return;
      }

      step++;
      const progress = Math.min(step / FADE_STEPS, 1);

      // Fade out current track
      if (audio && !audio.paused) {
        audio.volume = Math.max(0, startVolume * (1 - progress));
      }

      // Fade in next track
      if (nextAudio && !nextAudio.paused) {
        nextAudio.volume = Math.min(startVolume, startVolume * progress);
      }

      if (progress >= 1) {
        // Crossfade complete — stop current, hand off to loadAndPlay for next track
        if (crossfadeAnimRef.current !== null) {
          clearInterval(crossfadeAnimRef.current);
          crossfadeAnimRef.current = null;
        }

        // Pause and reset the crossfade preview audio
        if (nextAudio) {
          nextAudio.pause();
          nextAudio.volume = startVolume;
          nextAudio.src = '';
        }

        // Pause current audio
        audio.pause();
        audio.volume = startVolume;

        // Update index and delegate to loadAndPlay so all counters/interstitials fire
        setCurrentIndex(next);
        stopTicker();
        setElapsed(0);
        // useBuffered=false because we just cleared nextAudio.src
        loadAndPlay(next, undefined, false);
      }
    }, STEP_INTERVAL) as unknown as number;
  }, [songs, currentIndex, isPlaying, loadAndPlay, stopTicker]);

  // ── Seek ──────────────────────────────────────────────────────────────────
  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * duration;
    setElapsed(pct * duration);
  };

  const progressPct = duration > 0 ? Math.min((elapsed / duration) * 100, 100) : 0;

  const formatTime = (sec: number) => {
    if (!isFinite(sec) || isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const VolumeIcon = muted || volume === 0 ? VolumeX : volume < 50 ? Volume1 : Volume2;

  // ── Weather modal state ───────────────────────────────────────────────────
  const [showWeatherModal, setShowWeatherModal] = useState(false);

  // ── Artwork helpers ───────────────────────────────────────────────────────
  const getArtUrl = (song: Song | null): string => {
    if (!song) return '';
    return artworkCache[song.id] ?? '';
  };

  const currentArtUrl = getArtUrl(currentSong);
  const albumArtColors = getArtColor(
    currentSong ? currentSong.artist + currentSong.title : 'default'
  );
  const artistInitials = currentSong
    ? getInitials(currentSong.artist, currentSong.title)
    : '♪';

  return (
    <>
      <section className="public-hero-bg min-h-[70vh] flex flex-col">
        {/* Hidden audio elements — main + pre-buffer + DJ Jorge */}
        <audio
          ref={audioRef}
          preload="auto"
          onEnded={handleEnded}
          onTimeUpdate={handleTimeUpdate}
          onError={() => {
            setIsPlaying(false);
            setIsLoading(false);
            stopTicker();
            setLoadError('Audio error — skipping to next track.');
            if (songs.length > 1) {
              const next = (currentIndex + 1) % songs.length;
              setCurrentIndex(next);
              loadAndPlay(next);
            }
          }}
          onLoadedMetadata={() => {
            if (audioRef.current) setDuration(audioRef.current.duration || 0);
          }}
        />
        {/* Pre-buffer element */}
        <audio ref={nextAudioRef} preload="auto" style={{ display: 'none' }} />
        {/* DJ Jorge audio element */}
        <audio ref={djAudioRef} style={{ display: 'none' }} />
              {/* Jingle audio element */}
        <audio ref={jingleAudioRef} style={{ display: 'none' }} />
        {/* Station jingle audio element */}
        <audio ref={stationJingleAudioRef} style={{ display: 'none' }} />
        {/* Hits jingle audio element */}
        <audio ref={hitsJingleAudioRef} style={{ display: 'none' }} />
        {/* Weather MP3 audio element */}
        <audio ref={weatherAudioRef} style={{ display: 'none' }} />
        {/* News bulletin audio element */}
        <audio ref={newsAudioRef} style={{ display: 'none' }} />

        {/* Topbar */}
        <header className="flex items-center justify-between px-6 md:px-12 py-5 border-b border-border/50">
          <div className="flex items-center gap-3">
            <AppLogo size={34} />
            <span className="font-bold text-lg text-foreground tracking-tight">Rumbaexplosions</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-success/10 border border-success/20 rounded-full">
              <span className="w-2 h-2 rounded-full bg-success live-pulse" />
              <span className="text-xs font-semibold text-success">LIVE</span>
            </div>
            <a
              href="https://achactnews.onhercules.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost text-xs font-semibold"
            >
              Noticias
            </a>
            <Link href="/sign-up-login-screen" className="btn-ghost text-xs">Admin</Link>
          </div>
        </header>

        {/* Player */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 text-center">

          {/* DJ Jorge speaking indicator */}
          {djSpeaking && (
            <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/30 rounded-full animate-pulse">
              <span className="text-lg">🎙️</span>
              <span className="text-sm font-semibold text-accent">Jorge está hablando...</span>
              <div className="flex items-end gap-0.5 h-4">
                {[0.4, 0.7, 1.0, 0.6, 0.9].map((h, i) => (
                  <div
                    key={`dj-wave-${i}`}
                    className="w-1 rounded-full bg-accent"
                    style={{
                      height: `${h * 16}px`,
                      animation: `waveform 0.6s ease-in-out ${i * 0.1}s infinite alternate`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Weather announcement indicator */}
          {weatherSpeaking && (
            <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full animate-pulse">
              <span className="text-lg">🌤️</span>
              <span className="text-sm font-semibold text-blue-400">Reporte del clima en vivo...</span>
              <div className="flex items-end gap-0.5 h-4">
                {[0.5, 0.8, 0.6, 1.0, 0.7].map((h, i) => (
                  <div
                    key={`weather-wave-${i}`}
                    className="w-1 rounded-full bg-blue-400"
                    style={{
                      height: `${h * 16}px`,
                      animation: `waveform 0.6s ease-in-out ${i * 0.1}s infinite alternate`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* News bulletin indicator */}
          {newsSpeaking && (
            <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full animate-pulse">
              <span className="text-lg">📰</span>
              <span className="text-sm font-semibold text-amber-400">Boletín de noticias globales...</span>
              <div className="flex items-end gap-0.5 h-4">
                {[0.6, 0.9, 0.5, 1.0, 0.7, 0.4, 0.8].map((h, i) => (
                  <div
                    key={`news-wave-${i}`}
                    className="w-1 rounded-full bg-amber-400"
                    style={{
                      height: `${h * 16}px`,
                      animation: `waveform 0.6s ease-in-out ${i * 0.1}s infinite alternate`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Album Art */}
          <div className="relative mb-6">
            {/* Glow ring when playing */}
            <div
              className={`absolute inset-0 rounded-2xl transition-opacity duration-700 ${isPlaying ? 'opacity-60' : 'opacity-0'}`}
              style={{
                background: `radial-gradient(circle, ${albumArtColors[0]}55 0%, transparent 70%)`,
                transform: 'scale(1.15)',
                filter: 'blur(16px)',
              }}
            />

            {/* Art square */}
            <div
              className={`relative w-40 h-40 md:w-52 md:h-52 rounded-2xl flex items-center justify-center shadow-2xl transition-transform duration-500 overflow-hidden ${isPlaying ? 'scale-100' : 'scale-95'}`}
              style={
                currentArtUrl
                  ? {}
                  : { background: `linear-gradient(135deg, ${albumArtColors[0]}, ${albumArtColors[1]})` }
              }
            >
              {currentArtUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={currentArtUrl}
                  alt={currentSong ? `${currentSong.artist} – ${currentSong.title}` : 'Album art'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    if (currentSong) {
                      setArtworkCache((prev) => ({ ...prev, [currentSong.id]: '' }));
                    }
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <>
                  <div
                    className="absolute inset-0 rounded-2xl opacity-10"
                    style={{
                      backgroundImage:
                        'repeating-radial-gradient(circle at 50% 50%, transparent 0px, transparent 8px, rgba(0,0,0,0.3) 9px, transparent 10px)',
                    }}
                  />
                  <span className="relative text-white font-black text-4xl md:text-5xl tracking-tight select-none drop-shadow-lg">
                    {artistInitials}
                  </span>
                </>
              )}

              {/* Spinning disc indicator when playing */}
              {isPlaying && (
                <div
                  className="absolute bottom-3 right-3 w-7 h-7 rounded-full border-2 border-white/30 flex items-center justify-center"
                  style={{ animation: 'spin 3s linear infinite' }}
                >
                  <div className="w-2 h-2 rounded-full bg-white/60" />
                </div>
              )}
            </div>
          </div>

          {/* Waveform */}
          <div className={`flex items-end justify-center gap-1 h-8 mb-5 transition-opacity duration-300 ${isPlaying ? 'opacity-100' : 'opacity-20'}`}>
            {[0.3,0.6,0.9,0.5,0.8,1.0,0.7,0.4,0.9,0.6,0.8,0.3,0.7,0.5,1.0,0.6,0.4,0.8,0.9,0.5].map((h, i) => (
              <div
                key={`wave-${i}`}
                className={`rounded-full transition-all duration-300 ${isPlaying ? 'waveform-bar' : ''}`}
                style={{
                  width: '4px',
                  height: `${h * 32}px`,
                  background: 'linear-gradient(to top, var(--primary), var(--accent))',
                  animationDelay: `${i * 0.08}s`,
                }}
              />
            ))}
          </div>

          {/* Track info */}
          {currentSong ? (
            <div className="mb-6 w-full max-w-lg">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="flex items-center gap-1 text-xs font-bold text-success bg-success/10 border border-success/20 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-success live-pulse" />
                  ON AIR
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-1">{currentSong.title}</h1>
              {currentSong.artist && currentSong.artist !== 'Unknown Artist' && (
                <p className="text-base text-muted-foreground mb-1">{currentSong.artist}</p>
              )}

              {/* Progress bar */}
              <div className="w-full max-w-sm mx-auto mb-1 cursor-pointer" onClick={handleSeek}>
                <div className="h-1.5 bg-border rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs font-mono text-muted-foreground mt-1">
                  <span>{formatTime(elapsed)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            </div>
          ) : songs.length === 0 ? (
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent mb-2">Live Stream</p>
              <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-2">Rumbaexplosions Live</h1>
              <p className="text-lg text-muted-foreground">Broadcasting 24/7 · Tune in now</p>
            </div>
          ) : (
            <div className="mb-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent mb-2">Loading…</p>
            </div>
          )}

          {/* Error message */}
          {loadError && <p className="text-xs text-red-400 mb-3">{loadError}</p>}

          {/* Controls */}
          <div className="flex items-center gap-5 mb-4">
            {/* Volume */}
            <div className="flex items-center gap-2">
              <button onClick={() => setMuted((m) => !m)} className="btn-ghost p-2" aria-label={muted ? 'Unmute' : 'Mute'}>
                <VolumeIcon size={18} />
              </button>
              <input
                type="range" min={0} max={100} value={muted ? 0 : volume}
                onChange={(e) => {
                  setVolumeState(Number(e.target.value));
                  setMuted(false);
                  if (audioRef.current) audioRef.current.volume = Number(e.target.value) / 100;
                }}
                className="w-20 accent-primary"
              />
            </div>

            {/* Skip Prev */}
            <button onClick={handleSkipPrev} disabled={songs.length < 2} className="btn-ghost p-2 disabled:opacity-30" title="Previous track">
              <SkipBack size={18} />
            </button>

            {/* Play/Pause */}
            <button
              onClick={handlePlayPause}
              disabled={isLoading || songs.length === 0}
              className="w-16 h-16 rounded-full bg-primary flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all duration-150 shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
              style={{ boxShadow: '0 0 24px rgba(124, 58, 237, 0.5)' }}
            >
              {isLoading ? (
                <svg className="animate-spin w-6 h-6 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : isPlaying ? (
                <Pause size={24} className="text-white" />
              ) : (
                <Play size={24} className="text-white ml-1" />
              )}
            </button>

            {/* Skip Next */}
            <button onClick={handleSkipNext} disabled={songs.length < 2} className="btn-ghost p-2 disabled:opacity-30" title="Next track">
              <SkipForward size={18} />
            </button>

            {/* Share */}
            <button className="btn-ghost p-2" title="Share">
              <ExternalLink size={18} />
            </button>
          </div>

          {songs.length === 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              No tracks uploaded yet — add music via the admin Music Library
            </p>
          )}

          {/* DJ Jorge badge */}
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 mb-1">
            <span>🎙️</span>
            <span>DJ Jorge speaks every {DJ_INTERVAL} songs</span>
          </div>

          {/* Medellín Weather Button */}
          <button
            onClick={() => setShowWeatherModal(true)}
            className="flex items-center gap-1.5 text-xs font-semibold text-sky-400 hover:text-sky-300 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 px-3 py-1.5 rounded-full transition-all duration-150 mt-1 mb-1"
          >
            <span>🌤️</span>
            <span>Medellín Weather</span>
          </button>

          {/* Queue toggle */}
          {songs.length > 0 && (
            <button
              onClick={() => setShowQueue((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mt-2"
            >
              <Music2 size={12} />
              <span>{songs.length} song{songs.length !== 1 ? 's' : ''} in queue</span>
              {showQueue ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          )}

          {/* Song Queue Panel */}
          {showQueue && songs.length > 0 && (
            <div className="mt-4 w-full max-w-md bg-card border border-border rounded-2xl overflow-hidden text-left">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground uppercase tracking-wider">Queue</p>
                <button
                  onClick={handleShuffle}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                  title="Shuffle queue"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 3 21 3 21 8"/>
                    <line x1="4" y1="20" x2="21" y2="3"/>
                    <polyline points="21 16 21 21 16 21"/>
                    <line x1="15" y1="15" x2="21" y2="21"/>
                  </svg>
                  <span>Shuffle</span>
                </button>
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: '220px' }}>
                {songs.map((song, idx) => {
                  const isCurrent = idx === currentIndex;
                  const qColors = getArtColor(song.artist + song.title);
                  const qInitials = getInitials(song.artist, song.title);
                  const qArtUrl = getArtUrl(song);
                  return (
                    <div
                      key={`queue-${song.id}-${idx}`}
                      className={`flex items-center gap-3 px-4 py-2.5 border-b border-border/50 last:border-b-0 cursor-pointer transition-colors
                        ${isCurrent ? 'bg-primary/10' : 'hover:bg-muted/30'}`}
                      onClick={async () => {
                        setCurrentIndex(idx);
                        await loadAndPlay(idx);
                      }}
                    >
                      {/* Mini album art */}
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xs font-bold shadow overflow-hidden"
                        style={
                          qArtUrl
                            ? {}
                            : { background: `linear-gradient(135deg, ${qColors[0]}, ${qColors[1]})` }
                        }
                      >
                        {qArtUrl ? (
                          // eslint-disable-next-line @next/next/no-img-element
                          <img
                            src={qArtUrl}
                            alt={`${song.title} art`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              setArtworkCache((prev) => ({ ...prev, [song.id]: '' }));
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : isCurrent && isPlaying ? (
                          <span className="w-1.5 h-1.5 rounded-full bg-white live-pulse" />
                        ) : (
                          qInitials
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-semibold truncate ${isCurrent ? 'text-primary' : 'text-foreground'}`}>
                          {song.title}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{song.artist} · {song.genre}</p>
                      </div>
                      <span className="text-xs font-mono text-muted-foreground flex-shrink-0">{song.duration}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>

        {/* ── Tap-to-Play overlay — shown until user interacts (fixes autoplay policy) ── */}
        {!userHasInteracted && songs.length > 0 && (
          <div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(8px)' }}
          >
            <div className="flex flex-col items-center gap-6 px-8 text-center">
              <AppLogo size={56} />
              <h2 className="text-3xl font-bold text-white">Rumbaexplosions</h2>
              <p className="text-base text-white/70 max-w-xs">Tu estación favorita de música latina — en vivo 24/7</p>
              <button
                onClick={() => setUserHasInteracted(true)}
                className="mt-2 w-24 h-24 rounded-full bg-primary flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all duration-150 shadow-2xl"
                style={{ boxShadow: '0 0 40px rgba(124, 58, 237, 0.7)' }}
                aria-label="Tap to play"
              >
                <Play size={36} className="text-white ml-2" />
              </button>
              <p className="text-sm text-white/50">Toca para comenzar</p>
            </div>
          </div>
        )}

        {/* Loading overlay while songs are being fetched */}
        {!songsLoaded && (
          <div
            className="fixed inset-0 z-50 flex flex-col items-center justify-center"
            style={{ backgroundColor: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(8px)' }}
          >
            <div className="flex flex-col items-center gap-4 px-8 text-center">
              <AppLogo size={48} />
              <svg className="animate-spin w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-sm text-white/60">Cargando música...</p>
            </div>
          </div>
        )}

      </section>

      {/* Medellín Weather Modal */}
      {showWeatherModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}
          onClick={() => setShowWeatherModal(false)}
        >
          <div
            className="relative w-full max-w-sm"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowWeatherModal(false)}
              className="absolute -top-3 -right-3 z-10 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              aria-label="Close"
            >
              ✕
            </button>

            {/* Weather embed */}
            <div style={{ background: '#1a365d', borderRadius: '12px', padding: '20px', fontFamily: 'sans-serif', color: 'white' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Reporte del Clima - Medellin</h3>
              <p style={{ margin: '0 0 6px', fontSize: '14px', opacity: 0.8 }}>Medellin, Colombia</p>
              <audio
                controls
                style={{ width: '100%' }}
                src="https://sincere-armadillo-104.convex.site/audio/medellin"
              >
                Su navegador no soporta audio.
              </audio>
              <p style={{ margin: '12px 0 0', fontSize: '11px', opacity: 0.5 }}>Actualizado cada 2 horas</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}